import deepmerge from "deepmerge";
import { useMemo } from "react";
import { FabProps } from "@material-ui/core/Fab";
import { useAppContext } from "../../../../../lib/appContext";
import { usePromotion } from "../../../../../lib/promotionContext";
import { makeOverride, propertiesAllowed } from "./utils";
import { Button } from "../../../../../../config/widgets";

export type NormalizedButtonPropsCombined = Button & FabProps;

export const usePromotionConfig = () => {
  const { businessUnitConfig } = useAppContext();
  const promotionContext = usePromotion() || { widgets: {} };

  const businessWithPromotion = useMemo(
    () => deepmerge(businessUnitConfig, { widgets: promotionContext }),
    [promotionContext, businessUnitConfig]
  );
  return businessWithPromotion;
};

export const useBannerConfig = () => {
  const { widgets } = usePromotionConfig();
  return Boolean(widgets && widgets.banner) ? widgets.banner : null;
};

export const useWidgetHeroConfig = () => {
  const businessUnitConfig = usePromotionConfig();
  return businessUnitConfig.widgets.hero.config;
};

export const useWidgetMuiFabStyles = () => {
  const { primaryButton, secondaryButton } = useWidgetHeroConfig();
  const overridePrimary = makeOverride(primaryButton, propertiesAllowed);
  const overrideSecondary = makeOverride(secondaryButton, propertiesAllowed);

  return {
    MuiFab: {
      primary: overridePrimary,
      secondary: overrideSecondary
    }
  };
};

export const useWidgetMuiTextsStyles = () => {
  const { title, subtitle, description } = useWidgetHeroConfig();
  const allowed: any = { title, subtitle, description };
  const styles = useMemo(
    () =>
      Object.keys(allowed).reduce(
        (rules: any, key: string) => {
          if (allowed[key]) {
            const value = allowed[key];
            if (value.color) {
              rules[key] = { color: value.color };
            }
          }
          return rules;
        },
        { title: {}, subtitle: {}, description: {} }
      ),
    [title, subtitle, description]
  );
  return styles;
};

export enum ColorTypes {
  Title = "title",
  Subtitle = "subtitle",
  Description = "description"
}

export const useWidgetTextColorFromType = (type?: ColorTypes) => {
  const styles = useWidgetMuiTextsStyles();
  return type ? styles[type] || {} : {};
};

export const useBusinessWidgetBackgroundImages = () => {
  const { backgroundUrl, backgroundUrlSm } = useWidgetHeroConfig();
  return { backgroundUrl, backgroundUrlSm };
};

const normalizeButtonProps = (
  buttonProps: NormalizedButtonPropsCombined,
  businessUnit?: string
) => {
  const nextProps = Object.assign({}, buttonProps);
  nextProps.children = nextProps.label;
  nextProps.href = !Boolean(nextProps.href) ? businessUnit && `#${businessUnit}` : nextProps.href;
  return nextProps;
};

interface BorderStyleColor {
  borderColor?: string;
}

export const useBorderColorFilter = () => {
  const { simulatorStripeColor } = useWidgetHeroConfig();
  const borderStyleColor = useMemo(() => {
    const style: BorderStyleColor = {};
    if (simulatorStripeColor) {
      style.borderColor = simulatorStripeColor;
    }
    return style;
  }, [simulatorStripeColor]);
  return borderStyleColor;
};

export const useBusinessWidgetFiltersSelectors = () => {
  const { filters } = useWidgetHeroConfig();
  return filters && Array.isArray(filters.selectors) ? filters.selectors : [];
};

export const useBusinessWidgetButtons = () => {
  const { primaryButton, secondaryButton, terms } = useWidgetHeroConfig();
  const { businessCode } = usePromotionConfig();

  const primaryButtonMemoize = useMemo<NormalizedButtonPropsCombined>(
    () => primaryButton && normalizeButtonProps(primaryButton, businessCode),
    [primaryButton]
  );
  const secondaryButtonMemoize = useMemo<NormalizedButtonPropsCombined>(
    () => secondaryButton && normalizeButtonProps(secondaryButton),
    [secondaryButton]
  );

  return {
    terms,
    primaryButton: primaryButtonMemoize,
    secondaryButton: secondaryButtonMemoize
  };
};

export const useBusinessMainImage = () => {
  const { mainImageUrl } = useWidgetHeroConfig();
  return mainImageUrl;
};

export const useBusinessUnitName = () => {
  const { businessUnitConfig } = useAppContext();
  const { name } = businessUnitConfig;

  return name;
};

export const useBusinessHasAffiliate = () => {
  const { hasAffiliate } = useWidgetHeroConfig();
  return hasAffiliate;
};
